import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import type { UseRelativeTimeOptions } from "@/types/hooks";
import { useEffect, useMemo, useState } from "react";

dayjs.extend(relativeTime);

/**
 * Hook to format a date as relative time (e.g., "Updated 2 minutes ago")
 * and automatically update it at regular intervals
 * @param options - The options for the hook
 * @returns The formatted relative time string
 */
export function useRelativeTime({
  date,
  updateInterval = 60000,
  prefix = "Updated ",
  fallback = "Recently updated",
}: UseRelativeTimeOptions = {}) {
  const [_updateTrigger, setUpdateTrigger] = useState(0);

  const timeAgo = useMemo(() => {
    if (!date) return fallback;
    return `${prefix}${dayjs(date).fromNow()}`;
  }, [date, prefix, fallback, _updateTrigger]);

  useEffect(() => {
    const interval = setInterval(() => {
      setUpdateTrigger((prev) => prev + 1);
    }, updateInterval);

    return () => clearInterval(interval);
  }, [updateInterval]);

  return timeAgo;
}

